// Overlay of vote-count badges on every shape with at least one vote.
// Mounts inside the canvas container (not inside the Konva Stage) so it
// can use HTML/Tailwind. Re-renders on viewport pan/zoom by projecting
// each shape's bbox to screen coords.
//
// Visible during `vote`, `discuss`, and `actions` phases — voting itself
// happens in `vote`, but the indicators stay up through discussion so the
// room can see what's top-voted.

import * as Y from 'yjs';
import type { Shape } from '@frame/shared';
import { shapeBBox } from '../../canvas/binding/shapesMap';
import { useShapeVotes } from '../useVotes';

interface Props {
  doc: Y.Doc;
  shapes: Shape[];
  viewport: { x: number; y: number; scale: number };
}

export function VoteIndicators({ doc, shapes, viewport }: Props) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden z-[5]"
    >
      {shapes.map((shape) => (
        <VoteBadge
          key={shape.id}
          doc={doc}
          shape={shape}
          viewport={viewport}
        />
      ))}
    </div>
  );
}

function VoteBadge({
  doc,
  shape,
  viewport,
}: {
  doc: Y.Doc;
  shape: Shape;
  viewport: { x: number; y: number; scale: number };
}) {
  const votes = useShapeVotes(doc, shape.id);
  if (votes.total === 0) return null;
  const bbox = shapeBBox(shape);
  if (!bbox) return null;

  // Position the badge at the shape's top-right in screen coords. 12px outset
  // so it sits over the corner rather than inside the shape body.
  const screenX = viewport.x + (bbox.x + bbox.w) * viewport.scale;
  const screenY = viewport.y + bbox.y * viewport.scale;

  return (
    <div
      style={{ position: 'absolute', left: screenX - 14, top: screenY - 10 }}
      className="
        h-6 min-w-[24px] px-1.5 inline-flex items-center justify-center
        rounded-full bg-accent text-accent-fg
        text-12 font-semibold tabular-nums shadow-sm
      "
    >
      {votes.total}
    </div>
  );
}
